/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import {
  QuestionChangeOptionalProps,
  QuestionReducerState,
} from '@g17eco/types/questionInterfaces';
import { hasValueDataChanged } from '../../../utils/valueData';
import { getInputValue, getNumberScale, getUnitCode, getValueData, isNaOrNr } from '../../../utils/universalTrackerValue';

export const useHasValueChanged = (
  state: Pick<QuestionReducerState, 'value' | 'valueData' | 'table' | 'displayCheckbox'>,
  props: QuestionChangeOptionalProps & { isEmptyData?: boolean }
) => {
  const { value, valueData, displayCheckbox, table } = state;
  const { isEmptyData, utr, utrv } = props;

  return React.useMemo(() => {
    if (!utr || !utrv) {
      return false;
    }

    if (String(getInputValue(utrv)) !== String(value)) {
      return true;
    }

    if (isNaOrNr(utrv) && isEmptyData) {
      return false;
    }

    return hasValueDataChanged({
      currentValueData: getValueData(utrv, utr.getValueType()),
      nextValueData: valueData,
      displayCheckbox,
      table,
      valueType: utr.getValueType(),
    });
  }, [
    utrv,
    value,
    valueData,
    table,
    displayCheckbox,
    utr,
    isEmptyData
  ]);
};

export const useHasCommentsEvidenceChanged = (state: QuestionReducerState): boolean => {
  const { files, existingFiles, comments } = state;

  const filesHasChanged = React.useMemo(
    () => files.length !== 0 || existingFiles.findIndex(f => f.isDeleted || f.isUpdated) >= 0,
    [files, existingFiles]
  )

  return React.useMemo(() => {
    return filesHasChanged || String(comments).trim() !== ''
  },
    [
      comments,
      filesHasChanged
    ]);
};

export const useHasUnitNumberScaleChanged = (state: QuestionReducerState, props: QuestionChangeOptionalProps) => {
  const { unit, numberScale } = state;
  const { utrv, utr, surveyData } = props;
  const unitConfig = surveyData && 'unitConfig' in surveyData ? surveyData.unitConfig : undefined;
  const currentUnit = utrv && utr ? getUnitCode(utrv, utr, unitConfig) : undefined;
  const currentNumberScale = utrv && utr ? getNumberScale(utrv, utr, unitConfig) : undefined;

  return unit !== currentUnit || numberScale !== currentNumberScale;
};
